import { Form } from 'antd';
import { useCallback, useContext, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { useCreateClientByIntegrationMutation } from '@app/api/Client/api';
import { useLazySearchClientsByIntegrationQuery } from '@app/api/Clients/api';
import { useLazyGetClientLeadForViewQuery } from '@app/api/Leads/api';
import { LEAD_CARD_PATH } from '@app/constants';
import { useUserPrint } from '@app/hooks';
import { NotificationContext } from '@app/providers';
import { handleCatchError } from '@app/services';
import type { NClients, NLeads } from '@app/types';

import type { RadioChangeEvent } from 'antd';

interface UseWriteFirstModalProps {
  onCancel: () => void;
}

export const useWriteFirstModal = ({ onCancel }: UseWriteFirstModalProps) => {
  const [similarCustomers, setSimilarCustomers] = useState<
    NClients.ModelClientLite[]
  >([]);
  const [similarCustomersSelectedValue, setSimilarCustomersSelectedValue] =
    useState<number | undefined>();
  const [isNewClient, setIsNewClient] = useState(true);
  const [isLoading, setIsLoading] = useState(false);

  const [form] = Form.useForm();
  const { getFieldsValue, resetFields, submit } = form;

  const { activeBranchId } = useUserPrint();
  const { showNotification } = useContext(NotificationContext);
  const navigate = useNavigate();

  const [createClientByIntegration] = useCreateClientByIntegrationMutation();
  const [searchClientsByIntegration] = useLazySearchClientsByIntegrationQuery();
  const [getClientLeadForView] = useLazyGetClientLeadForViewQuery();

  const findDuplicate = useCallback(async () => {
    const {
      clientName,
      integrationId,
      userName,
    }: NLeads.WriteFirstModalFormValues = getFieldsValue();

    try {
      if (activeBranchId && integrationId && userName) {
        const similarCustomersResult = await searchClientsByIntegration({
          integrationId,
          userName: encodeURIComponent(userName),
          clientName: clientName || null,
          ActiveBranchId: activeBranchId,
        }).unwrap();
        setSimilarCustomers(similarCustomersResult);
      }
    } catch (error) {
      showNotification(handleCatchError(error));
    }
  }, [
    activeBranchId,
    getFieldsValue,
    searchClientsByIntegration,
    showNotification,
  ]);

  const finishForm = useCallback(
    (value: NLeads.WriteFirstModalFormValues) => {
      const { clientName, integrationId, userName } = value;

      const handleOperation = async (operation: Function) => {
        try {
          setIsLoading(true);
          const { Id } = await operation();
          navigate(
            `${LEAD_CARD_PATH}?leadId=${Id}&integrationId=${integrationId}`
          );
          onCancel();
        } catch (error) {
          showNotification(handleCatchError(error));
        } finally {
          setIsLoading(false);
          setIsNewClient(true);
          setSimilarCustomersSelectedValue(undefined);
          resetFields();
          setSimilarCustomers([]);
        }
      };

      handleOperation(async () => {
        if (isNewClient) {
          return await createClientByIntegration({
            ActiveBranchId: activeBranchId,
            data: {
              IntegrationId: integrationId,
              Phone: userName,
              ClientName: clientName || null,
            },
          }).unwrap();
        }

        if (similarCustomersSelectedValue) {
          return await getClientLeadForView({
            ActiveBranchId: activeBranchId,
            integrationId,
            clientId: similarCustomersSelectedValue,
          }).unwrap();
        }
      });
    },
    [
      navigate,
      onCancel,
      showNotification,
      resetFields,
      isNewClient,
      similarCustomersSelectedValue,
      createClientByIntegration,
      getClientLeadForView,
      activeBranchId,
    ]
  );

  const handleCancel = useCallback(() => {
    setSimilarCustomersSelectedValue(undefined);
    setSimilarCustomers([]);
    resetFields();
    onCancel();
  }, [onCancel, resetFields]);

  const handleSimilarCustomersChange = useCallback((e: RadioChangeEvent) => {
    setIsNewClient(false);
    setSimilarCustomersSelectedValue(e.target.value);
  }, []);

  const handleNewClientCheckboxClick = useCallback(() => {
    setIsNewClient(true);
    setSimilarCustomersSelectedValue(undefined);
  }, []);

  return {
    handleCancel,
    finishForm,
    findDuplicate,
    form,
    similarCustomers,
    handleSimilarCustomersChange,
    similarCustomersSelectedValue,
    isNewClient,
    handleNewClientCheckboxClick,
    submit,
    isLoading,
  };
};
